import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Camera,
    Film,
    Image as ImageIcon,
    Palette,
    Sparkles,
    Theater,
    Clapperboard,
    BookOpen,
    Layers,
    Flame,
    User,
    Music
} from 'lucide-react';

const creativeServices = [
    { id: '01', icon: Sparkles, title: 'Conceptual & Fine Art Stills', desc: 'Transforming abstract concepts, psychological themes, and surreal narratives into breathtaking standalone imagery.' },
    { id: '02', icon: Theater, title: 'Performance Art & Theater', desc: 'Capturing the raw tension, dramatic stage lighting, and fluid physical expression of live performance art.' },
    { id: '03', icon: Palette, title: 'Museum-Grade Art Reproduction', desc: 'Ultra-high-resolution, color-accurate archival capture of paintings, sculptures, and multi-media installations.' },
    { id: '04', icon: BookOpen, title: 'Editorial Author & Artist Portfolios', desc: 'Candid, mood-driven portraits that showcase your unique creative persona for lookbooks, press kits, and publications.' },
    { id: '05', icon: Clapperboard, title: 'Experimental Video & Shorts', desc: 'Poetic, boundary-pushing moving picture segments optimized for gallery displays or digital artistic previews.' },
    { id: '06', icon: Flame, title: 'Avant-Garde Composites', desc: 'Complex multi-exposure configurations and digital manipulations stitched together to build impossible realities.' },
];

const artsCultureServices = [
    { id: '01', icon: Music, title: 'Musician & Album Visuals', desc: 'Sonic identity translated to film. High-concept cover art and aesthetic content loops for distribution platforms.' },
    { id: '02', icon: Layers, title: 'Exhibition Opening Portfolios', desc: 'Documenting gallery launches, attendee interactions, spatial curations, and artist panel discussions.' },
    { id: '03', icon: Camera, title: 'Behind-The-Process Essays', desc: 'A photojournalistic dive inside the messy, brilliant space of artist studios, workshops, and rehearsals.' },
    { id: '04', icon: Palette, title: 'Fashion-As-Art Editorials', desc: 'Blurring the lines between textile engineering and fine art through unconventional lighting and sculpture posing.' },
    { id: '05', icon: ImageIcon, title: 'Limited Edition Print Mastering', desc: 'Pre-flight file configuration, tonal grading, and substrate matching tailored explicitly for luxury giclée printing.' },
];

const fineArtDeliverables = [
    { id: '01', icon: Sparkles, title: 'Sub-Pixel Color Profiling', desc: 'Utilizing strict studio calibrations to capture identical pigment matching for delicate artistic mediums.' },
    { id: '02', icon: Layers, title: 'High-Bit Dynamic Composites', desc: 'Rendering visual files in native 16-bit color channels to maximize dark-gradient fidelity and deep textures.' },
    { id: '03', icon: Theater, title: 'Motion Blur & Kinetic Capture', desc: 'Mastering drag-shutter techniques to translate long-exposure dance loops and theater movement gracefully.' },
    { id: '04', icon: Palette, title: 'Texture-Enhanced Surface Scans', desc: 'Raking strobe lighting configurations calculated to highlight the physical depth of impasto or canvas weaves.' },
    { id: '05', icon: Clapperboard, title: 'Gallery Projection Formats', desc: 'Optimizing continuous motion loops customized to match the dimensions of digital projection environments.' },
    { id: '06', icon: User, title: 'Immersive Casting & Staging', desc: 'Collaborating directly with actors, dancers, and models who excel in expressive physical theater styling.' },
    { id: '07', icon: Film, title: 'Cinematic Mood Teasers', desc: 'Sultry, atmospheric short-form video vignettes designed to preview upcoming exhibitions or project launches.' },
    { id: '08', icon: BookOpen, title: 'Monograph Layout Integration', desc: 'Formatting fine art images to sit perfectly within print publishing systems, books, and catalogs.' },
    { id: '09', icon: ImageIcon, title: 'Archival TIFF Asset Delivery', desc: 'Providing raw, uncompressed, ultra-high-resolution final assets structured for permanent digital preservation.' },
];

const CreativeandArts = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Creative & Arts Photography in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Discover creative and artistic photography by KhultStudios in Hyderabad. We specialize in conceptual photography, fine art portraits, editorial shoots, creative campaigns, surreal photography, and visual storytelling for individuals and brands."
                />

                <meta
                    name="keywords"
                    content="Creative Photography Hyderabad, Fine Art Photography Hyderabad, Artistic Photography, Conceptual Photography, Editorial Photography, Creative Portrait Photography, Visual Storytelling, Surreal Photography, Fine Art Portraits, Creative Photoshoot Hyderabad, Editorial Fashion Photography, Artistic Studio Photography, Creative Campaign Photography, Digital Art Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/creative-and-arts-photography"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/creative-and-arts-photography"
                />
                <meta
                    property="og:title"
                    content="Creative & Arts Photography in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Creative photography, conceptual art, editorial portraits, fine art photography, and visual storytelling by KhultStudios in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/creative-arts-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Creative & Arts Photography | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Professional creative, fine art, conceptual, and editorial photography services in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/creative-arts-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Creative & Fine Arts Visual Production
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Where Imaginative Concepts Meet Tangible Film
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Art isn't merely observed; it's felt. At KhultStudio, we act as visual extensions for independent creators, performance artists, and gallery curators—translating complex conceptual thoughts into breathtaking digital realities and archival portfolios.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Utilizing deep-shadow strobe architecture, ultra-high-resolution texture scanning, and kinetic motion tracking, we manifest imagery designed to challenge perspective, elevate exhibition design, and preserve creative craft eternally.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Vision
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=500" alt="Fine Art Abstract Oil Painting Session" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?q=80&w=500" alt="Conceptual Deep Moody Surreal Shadow Capture" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=500" alt="Sculpture Craft Installation Gallery Setup" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=500" alt="Live Musician Performance Lighting Close Up" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=500" alt="Fine Art Abstract Oil Painting Session Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?q=80&w=500" alt="Conceptual Deep Moody Surreal Shadow Capture Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=500" alt="Sculpture Craft Installation Gallery Setup Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=500" alt="Live Musician Performance Lighting Close Up Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography2.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography3.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography4.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography5.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography6.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography7.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/creative-and-arts/creative-and-arts-photography8.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* CREATIVE PHOTOGRAPHY SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">🔮 High-Concept Creative & Fine Art Production</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Visualizing the unseeable requires refined technique. Our creative framework approaches every frame as an independent masterpiece, blending experimental strobe work, dynamic textures, and complex storytelling matrices.
                    </p>
                    <div className="row g-4 mt-2">
                        {creativeServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ARTS & CULTURE CULTURE PORTFOLIOS */}
            <section className="wedding-services-section py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="text-white">🎨 Institutional Arts & Culture Documentation</h2>
                        <p className="text-light-gray mt-3 max-w-2xl mx-auto">
                            Preserving the momentum of fine arts demands absolute fidelity. From pixel-precise painting reproduction to documenting intense gallery openings and behind-the-scenes artist workflows, we protect the raw aesthetic integrity of your craft.
                        </p>
                    </div>

                    <div className="row g-4 justify-content-center">
                        {artsCultureServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINE ART OPERATIONAL COMPREHENSIVE MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Complete Fine Art Production Deliverables</h2>
                    <div className="row g-4">
                        {fineArtDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CreativeandArts;